export interface Question {
  questionNumber: number;
  question: string;
  answerOptions: {
    text: string;
    rationale: string;
    isCorrect: boolean;
  }[];
  hint: string;
}

export const practiceTestQuestions: Question[] = [
  {
    "questionNumber": 1,
    "question": "What is the primary purpose of Florida's real estate license law?",
    "answerOptions": [
      {
        "text": "To protect licensed real estate brokers from competition.",
        "rationale": "The purpose of the law is to protect the general public, not to protect licensees.",
        "isCorrect": false
      },
      {
        "text": "To regulate property values and ensure stable housing prices.",
        "rationale": "The law regulates professionals and brokerage activities, not property values.",
        "isCorrect": false
      },
      {
        "text": "To protect the public from dishonest or incompetent real estate professionals.",
        "rationale": "The Florida Real Estate Commission (FREC) and license law are designed to protect consumers by ensuring licensees meet ethical and competency standards.",
        "isCorrect": true
      },
      {
        "text": "To generate state revenue through licensing fees and fines.",
        "rationale": "While the state collects fees and fines, the primary purpose is consumer protection.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the role of the Florida Real Estate Commission (FREC) and who it serves."
  },
  {
    "questionNumber": 2,
    "question": "Which of the following is an example of an involuntary inactive license?",
    "answerOptions": [
      {
        "text": "A licensee who is on temporary medical leave and has notified the DBPR.",
        "rationale": "A licensee on medical leave would likely have their license in a voluntarily inactive status, not involuntarily.",
        "isCorrect": false
      },
      {
        "text": "A licensee who has failed to complete the required continuing education by the renewal deadline.",
        "rationale": "Failure to renew a license or complete mandatory continuing education results in the license becoming involuntarily inactive.",
        "isCorrect": true
      },
      {
        "text": "A broker who has retired and has chosen not to renew their license.",
        "rationale": "This describes a voluntarily inactive status, as the licensee made a choice not to renew.",
        "isCorrect": false
      },
      {
        "text": "A sales associate whose license has been revoked by the FREC.",
        "rationale": "A revoked license is 'null and void' and cannot be reactivated; it is not considered involuntarily inactive.",
        "isCorrect": false
      }
    ],
    "hint": "Involuntary means the status was not by choice."
  },
  {
    "questionNumber": 3,
    "question": "A licensed real estate broker is preparing an advertisement for a property. Which of the following is a legal requirement for the advertisement?",
    "answerOptions": [
      {
        "text": "It must include the broker's personal phone number.",
        "rationale": "While it can include a personal number, it is not a legal requirement.",
        "isCorrect": false
      },
      {
        "text": "It must be a 'blind ad' that does not mention the brokerage.",
        "rationale": "Blind ads are illegal in Florida. The advertisement must clearly state the licensed name of the brokerage firm.",
        "isCorrect": false
      },
      {
        "text": "It must include the licensed name of the brokerage firm.",
        "rationale": "All advertisements must include the licensed name of the brokerage to prevent misleading the public.",
        "isCorrect": true
      },
      {
        "text": "It must state the specific commission rate for the transaction.",
        "rationale": "Commissions are negotiable and do not need to be stated in an advertisement.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the purpose of advertising rules\u2014transparency for the consumer."
  },
  {
    "questionNumber": 4,
    "question": "A sales associate receives an earnest money deposit from a buyer on a Friday. When must the sales associate deliver the funds to their broker?",
    "answerOptions": [
      {
        "text": "By the end of the next business day.",
        "rationale": "A sales associate must immediately deliver the funds to their broker, which means by the end of the next business day. The broker then has until the end of the third business day to deposit the funds.",
        "isCorrect": true
      },
      {
        "text": "Within 24 hours of receipt.",
        "rationale": "The law specifies the end of the next business day, which is not necessarily within 24 hours.",
        "isCorrect": false
      },
      {
        "text": "Within 48 hours of receipt.",
        "rationale": "This is not the standard specified by Florida law.",
        "isCorrect": false
      },
      {
        "text": "By the end of the third business day.",
        "rationale": "This is the timeframe for the broker to deposit the funds into an escrow account, not for the sales associate to deliver them to the broker.",
        "isCorrect": false
      }
    ],
    "hint": "Remember the two separate deadlines: one for the sales associate and one for the broker."
  },
  {
    "questionNumber": 5,
    "question": "Which of the following is an example of a kickback under the Real Estate Settlement Procedures Act (RESPA)?",
    "answerOptions": [
      {
        "text": "A broker receiving a referral fee from a licensed real estate professional.",
        "rationale": "Referral fees between licensed professionals are generally permitted.",
        "isCorrect": false
      },
      {
        "text": "A broker receiving a commission for the sale of a property.",
        "rationale": "This is a standard, earned fee for services rendered.",
        "isCorrect": false
      },
      {
        "text": "A title company giving a broker a percentage of their title fees in exchange for a referral without a service being rendered.",
        "rationale": "RESPA prohibits unearned fees for referrals, which is what a kickback is.",
        "isCorrect": true
      },
      {
        "text": "A broker receiving a fee for a comparative market analysis (CMA).",
        "rationale": "This is a fee for a specific service and is not considered a kickback.",
        "isCorrect": false
      }
    ],
    "hint": "A kickback is an unearned fee."
  },
  {
    "questionNumber": 6,
    "question": "What is the primary characteristic of an exclusive right-of-sale listing agreement?",
    "answerOptions": [
      {
        "text": "The seller can sell the property themselves without paying a commission.",
        "rationale": "This describes an exclusive-agency listing, not an exclusive right-of-sale.",
        "isCorrect": false
      },
      {
        "text": "Multiple brokers are authorized to sell the property.",
        "rationale": "This describes an open listing, not an exclusive right-of-sale.",
        "isCorrect": false
      },
      {
        "text": "The broker earns a commission regardless of who sells the property.",
        "rationale": "In an exclusive right-of-sale agreement, the broker is guaranteed a commission, making it the most advantageous for the broker.",
        "isCorrect": true
      },
      {
        "text": "The broker is guaranteed a commission only if another broker sells the property.",
        "rationale": "This is not a recognized type of listing agreement.",
        "isCorrect": false
      }
    ],
    "hint": "This type of listing offers the most protection for the broker."
  },
  {
    "questionNumber": 7,
    "question": "A buyer and a seller sign a purchase and sale agreement. The contract is in what stage?",
    "answerOptions": [
      {
        "text": "Voidable",
        "rationale": "A voidable contract is valid but can be voided by a party, often due to duress or being a minor. This doesn't describe the stage of a normal contract.",
        "isCorrect": false
      },
      {
        "text": "Executed",
        "rationale": "An executed contract is one where all parties have fulfilled their obligations. This happens after the closing.",
        "isCorrect": false
      },
      {
        "text": "Unenforceable",
        "rationale": "An unenforceable contract may be valid, but it cannot be enforced by a court (e.g., an oral real estate contract). This is not the correct stage.",
        "isCorrect": false
      },
      {
        "text": "Executory",
        "rationale": "An executory contract is one where the terms have been agreed upon, but one or more parties have not yet fulfilled their obligations. This is the stage between signing and closing.",
        "isCorrect": true
      }
    ],
    "hint": "This describes a contract that is 'in progress' and not yet completed."
  },
  {
    "questionNumber": 8,
    "question": "What is a 'capitalization rate' primarily used for in real estate appraisal?",
    "answerOptions": [
      {
        "text": "To determine the tax rate on a property.",
        "rationale": "The tax rate is known as the millage rate and is not determined by the capitalization rate.",
        "isCorrect": false
      },
      {
        "text": "To estimate the value of an income-producing property.",
        "rationale": "The capitalization rate is a key component of the income capitalization approach to appraisal, which is used for income-producing properties.",
        "isCorrect": true
      },
      {
        "text": "To calculate the loan-to-value ratio for a mortgage.",
        "rationale": "The LTV ratio is calculated by dividing the loan amount by the property's value.",
        "isCorrect": false
      },
      {
        "text": "To measure the rate of inflation in the housing market.",
        "rationale": "The capitalization rate is a measure of return on investment, not inflation.",
        "isCorrect": false
      }
    ],
    "hint": "This is part of the Income Capitalization Approach."
  },
  {
    "questionNumber": 9,
    "question": "Which of the following is an example of economic obsolescence?",
    "answerOptions": [
      {
        "text": "A leaky roof that needs to be replaced.",
        "rationale": "This is an example of physical deterioration.",
        "isCorrect": false
      },
      {
        "text": "An outdated kitchen with appliances from the 1970s.",
        "rationale": "This is an example of functional obsolescence.",
        "isCorrect": false
      },
      {
        "text": "A busy highway being constructed next to the property.",
        "rationale": "Economic obsolescence is a loss in value due to factors outside of the property, such as a negative environmental change.",
        "isCorrect": true
      },
      {
        "text": "A house with a single bathroom in a neighborhood where all homes have at least two.",
        "rationale": "This is a form of functional obsolescence, as the property is outdated relative to its market.",
        "isCorrect": false
      }
    ],
    "hint": "This type of depreciation is incurable because it is outside the property line."
  },
  {
    "questionNumber": 10,
    "question": "A buyer and seller agree to a contract, but the buyer later discovers a material defect in the property that the seller knowingly concealed. The contract is considered:",
    "answerOptions": [
      {
        "text": "Valid and enforceable.",
        "rationale": "The concealment of a material fact would make the contract voidable.",
        "isCorrect": false
      },
      {
        "text": "Void.",
        "rationale": "A void contract has no legal effect from the beginning. This contract was valid when formed.",
        "isCorrect": false
      },
      {
        "text": "Unenforceable.",
        "rationale": "The contract is not unenforceable due to its form or content; it is voidable due to fraud.",
        "isCorrect": false
      },
      {
        "text": "Voidable.",
        "rationale": "Because the seller engaged in fraud, the buyer has the right to void the contract.",
        "isCorrect": true
      }
    ],
    "hint": "The contract can be canceled by the party who was wronged."
  },
  {
    "questionNumber": 11,
    "question": "A tenant holds over in a property after their lease has expired without the landlord's consent. This is an example of what type of tenancy?",
    "answerOptions": [
      {
        "text": "Tenancy for years",
        "rationale": "A tenancy for years has a definite start and end date.",
        "isCorrect": false
      },
      {
        "text": "Tenancy at will",
        "rationale": "A tenancy at will is with the landlord's permission and has no specific end date.",
        "isCorrect": false
      },
      {
        "text": "Tenancy at sufferance",
        "rationale": "A tenancy at sufferance is when a tenant stays in possession without the landlord's consent after the lease has expired.",
        "isCorrect": true
      },
      {
        "text": "Tenancy in common",
        "rationale": "Tenancy in common is a form of co-ownership, not a leasehold estate.",
        "isCorrect": false
      }
    ],
    "hint": "The tenant is 'suffering' the consequences of overstaying."
  },
  {
    "questionNumber": 12,
    "question": "What is the primary difference between a general warranty deed and a quitclaim deed?",
    "answerOptions": [
      {
        "text": "A general warranty deed can only be used for residential property.",
        "rationale": "A general warranty deed can be used for any type of real property.",
        "isCorrect": false
      },
      {
        "text": "A quitclaim deed transfers ownership, while a general warranty deed does not.",
        "rationale": "Both types of deeds transfer ownership.",
        "isCorrect": false
      },
      {
        "text": "A quitclaim deed offers a strong guarantee of title.",
        "rationale": "A quitclaim deed provides no guarantees or warranties about the title.",
        "isCorrect": false
      },
      {
        "text": "A general warranty deed provides the most comprehensive title protection to the grantee.",
        "rationale": "A general warranty deed contains all the covenants that protect the grantee's title, unlike a quitclaim deed which offers no protection.",
        "isCorrect": true
      }
    ],
    "hint": "Think about the level of protection each deed offers the buyer."
  },
  {
    "questionNumber": 13,
    "question": "What is a 'plat map' primarily used for in real estate?",
    "answerOptions": [
      {
        "text": "To show the location of a specific street address in a city.",
        "rationale": "A street map or GPS is used for this purpose.",
        "isCorrect": false
      },
      {
        "text": "To legally describe a property in the metes and bounds system.",
        "rationale": "Metes and bounds uses a series of directions and distances, not a plat map.",
        "isCorrect": false
      },
      {
        "text": "To show the lot and block numbers of a subdivision for legal description.",
        "rationale": "The lot and block method relies on a plat map to legally describe properties within a subdivision.",
        "isCorrect": true
      },
      {
        "text": "To illustrate the floor plan of a home for a buyer.",
        "rationale": "A floor plan is a blueprint or drawing, not a plat map.",
        "isCorrect": false
      }
    ],
    "hint": "This document is used for subdivisions."
  },
  {
    "questionNumber": 14,
    "question": "In Florida, what is the mandatory educational requirement for a sales associate before their first license renewal?",
    "answerOptions": [
      {
        "text": "14 hours of continuing education.",
        "rationale": "This is the continuing education requirement for subsequent renewals, not the first.",
        "isCorrect": false
      },
      {
        "text": "45 hours of post-licensing education.",
        "rationale": "A sales associate must complete 45 hours of post-licensing education before their first license renewal.",
        "isCorrect": true
      },
      {
        "text": "72 hours of pre-licensing education.",
        "rationale": "This is the pre-licensing course requirement for a broker.",
        "isCorrect": false
      },
      {
        "text": "24 hours of continuing education.",
        "rationale": "This is not a specific requirement for Florida sales associates.",
        "isCorrect": false
      }
    ],
    "hint": "This is an important requirement for new licensees."
  },
  {
    "questionNumber": 15,
    "question": "A broker is found to have committed 'conversion' of a client's funds. What does this mean?",
    "answerOptions": [
      {
        "text": "The broker failed to deposit the funds in a timely manner.",
        "rationale": "This is a failure to deposit funds promptly, but not necessarily conversion.",
        "isCorrect": false
      },
      {
        "text": "The broker deposited the client's funds into their personal business account.",
        "rationale": "This is an example of commingling.",
        "isCorrect": false
      },
      {
        "text": "The broker used the client's funds for personal or business expenses.",
        "rationale": "Conversion is the illegal use of a client's funds for a broker's own purposes.",
        "isCorrect": true
      },
      {
        "text": "The broker transferred the funds to a different escrow account.",
        "rationale": "This is not conversion, provided the transfer was authorized and for a legitimate purpose.",
        "isCorrect": false
      }
    ],
    "hint": "This is a more severe violation than commingling."
  },
  {
    "questionNumber": 16,
    "question": "A broker is representing a buyer in a single agency relationship. What is a key fiduciary duty owed to the client that a transaction broker does not owe?",
    "answerOptions": [
      {
        "text": "Accounting for all funds.",
        "rationale": "All brokerage relationships owe the duty of accounting for all funds.",
        "isCorrect": false
      },
      {
        "text": "Honesty and fair dealing.",
        "rationale": "All brokerage relationships owe the duty of honesty and fair dealing.",
        "isCorrect": false
      },
      {
        "text": "Limited confidentiality.",
        "rationale": "A transaction broker has a duty of limited confidentiality, not a single agent.",
        "isCorrect": false
      },
      {
        "text": "Loyalty.",
        "rationale": "The duty of loyalty is a fundamental component of a single agency relationship and is not owed by a transaction broker.",
        "isCorrect": true
      }
    ],
    "hint": "This duty requires the broker to put the client's interests first."
  },
  {
    "questionNumber": 17,
    "question": "What is the primary purpose of a 'title search'?",
    "answerOptions": [
      {
        "text": "To determine the property's market value for a mortgage.",
        "rationale": "An appraisal is used to determine a property's market value.",
        "isCorrect": false
      },
      {
        "text": "To identify any liens, encumbrances, or title defects on a property.",
        "rationale": "A title search is conducted to discover any 'clouds on the title' that could affect ownership.",
        "isCorrect": true
      },
      {
        "text": "To inspect the physical condition of the property for a buyer.",
        "rationale": "A home inspection is performed to assess the physical condition of a property.",
        "isCorrect": false
      },
      {
        "text": "To determine the property's legal description.",
        "rationale": "The legal description is already a part of the public record and is found in the deed, not through a title search.",
        "isCorrect": false
      }
    ],
    "hint": "A title search is done before a title insurance policy is issued."
  },
  {
    "questionNumber": 18,
    "question": "A home is sold for $400,000, and the buyer assumes the seller's existing mortgage of $250,000. How much is the Documentary Stamp Tax on the deed?",
    "answerOptions": [
      {
        "text": "$1,750",
        "rationale": "This would be the amount if the tax was based on the new mortgage.",
        "isCorrect": false
      },
      {
        "text": "$2,800",
        "rationale": "The Documentary Stamp Tax on the deed is based on the total purchase price, which is $400,000. ($400,000 / 100) * $0.70 = $2,800.",
        "isCorrect": true
      },
      {
        "text": "$875",
        "rationale": "This is the Intangible Tax.",
        "isCorrect": false
      },
      {
        "text": "$0",
        "rationale": "The tax is not zero; it's based on the total purchase price, even if the mortgage is assumed.",
        "isCorrect": false
      }
    ],
    "hint": "The Documentary Stamp Tax on the deed is always based on the full purchase price."
  },
  {
    "questionNumber": 19,
    "question": "Which of the following is a legal requirement for a deed in Florida?",
    "answerOptions": [
      {
        "text": "It must be signed by the grantee.",
        "rationale": "The grantee does not need to sign the deed.",
        "isCorrect": false
      },
      {
        "text": "It must be recorded within 30 days of the closing.",
        "rationale": "There is no legal requirement for a deed to be recorded, but it is necessary to provide constructive notice.",
        "isCorrect": false
      },
      {
        "text": "It must contain a street address as the legal description.",
        "rationale": "A street address is not a legally sufficient description.",
        "isCorrect": false
      },
      {
        "text": "It must be signed by two witnesses.",
        "rationale": "Florida law requires that a deed be signed by two witnesses.",
        "isCorrect": true
      }
    ],
    "hint": "Think about the formal requirements for a deed to be legally valid in Florida."
  },
  {
    "questionNumber": 20,
    "question": "What is the primary role of the Department of Business and Professional Regulation (DBPR)?",
    "answerOptions": [
      {
        "text": "To act as a quasi-judicial body that enforces real estate law.",
        "rationale": "This is the primary role of the Florida Real Estate Commission (FREC).",
        "isCorrect": false
      },
      {
        "text": "To issue real estate licenses and regulate professions in Florida.",
        "rationale": "The DBPR is the state agency that licenses and regulates professions, including real estate.",
        "isCorrect": true
      },
      {
        "text": "To represent the public in legal disputes with licensees.",
        "rationale": "The DBPR is a regulatory body, not a legal representative for the public.",
        "isCorrect": false
      },
      {
        "text": "To handle all legal and administrative hearings for licensees.",
        "rationale": "This is a function of the Florida Division of Administrative Hearings.",
        "isCorrect": false
      }
    ],
    "hint": "Think about the 'B' and 'P' in DBPR."
  },
  {
    "questionNumber": 21,
    "question": "What is 'physical deterioration' in real estate appraisal?",
    "answerOptions": [
      {
        "text": "A loss in value due to external factors like a new airport nearby.",
        "rationale": "This is economic obsolescence.",
        "isCorrect": false
      },
      {
        "text": "A loss in value due to a property's outdated design or features.",
        "rationale": "This is functional obsolescence.",
        "isCorrect": false
      },
      {
        "text": "A loss in value from normal wear and tear on a building.",
        "rationale": "Physical deterioration is the result of aging and wear and tear, such as a worn-out roof or cracked foundation.",
        "isCorrect": true
      },
      {
        "text": "A loss in value due to poor maintenance by the owner.",
        "rationale": "This is an example of physical deterioration, but it's not the complete definition.",
        "isCorrect": false
      }
    ],
    "hint": "This type of depreciation is often curable."
  },
  {
    "questionNumber": 22,
    "question": "What is the purpose of the 'rescission' of a contract?",
    "answerOptions": [
      {
        "text": "To transfer the contract's obligations to a new party.",
        "rationale": "This is the definition of an assignment.",
        "isCorrect": false
      },
      {
        "text": "To terminate the contract and return the parties to their original positions.",
        "rationale": "Rescission is the cancellation of a contract, with the goal of undoing the transaction.",
        "isCorrect": true
      },
      {
        "text": "To change or modify the terms of the contract.",
        "rationale": "This is the purpose of an amendment or an addendum.",
        "isCorrect": false
      },
      {
        "text": "To force a party to fulfill their obligations under the contract.",
        "rationale": "This is the purpose of specific performance.",
        "isCorrect": false
      }
    ],
    "hint": "Think of what happens to a contract when a party has a 'right to rescind'."
  },
  {
    "questionNumber": 23,
    "question": "Which type of mortgage is not guaranteed or insured by a government agency?",
    "answerOptions": [
      {
        "text": "VA loan",
        "rationale": "VA loans are guaranteed by the Department of Veterans Affairs.",
        "isCorrect": false
      },
      {
        "text": "FHA loan",
        "rationale": "FHA loans are insured by the Federal Housing Administration.",
        "isCorrect": false
      },
      {
        "text": "Conventional loan",
        "rationale": "Conventional loans are not backed by a government agency.",
        "isCorrect": true
      },
      {
        "text": "Reverse mortgage",
        "rationale": "A reverse mortgage can be FHA-insured, so it is not necessarily non-government.",
        "isCorrect": false
      }
    ],
    "hint": "This type of loan often requires private mortgage insurance (PMI)."
  },
  {
    "questionNumber": 24,
    "question": "Which of the following is an example of 'commingling'?",
    "answerOptions": [
      {
        "text": "A broker depositing a buyer's earnest money into their personal checking account.",
        "rationale": "Mixing a client's funds with a broker's own personal or business funds is commingling.",
        "isCorrect": true
      },
      {
        "text": "A broker using a client's escrow funds to pay office rent.",
        "rationale": "This is a more severe offense known as conversion.",
        "isCorrect": false
      },
      {
        "text": "A sales associate holding a client's escrow funds in a personal safe for a week.",
        "rationale": "This is a failure to properly handle funds, but it is not commingling.",
        "isCorrect": false
      },
      {
        "text": "A broker depositing a client's funds into a separate, interest-bearing escrow account with written consent.",
        "rationale": "This is a legal way to handle a client's funds.",
        "isCorrect": false
      }
    ],
    "hint": "This is the act of 'mixing' money."
  },
  {
    "questionNumber": 25,
    "question": "What is the legal term for a broker's duty to protect a client from harm?",
    "answerOptions": [
      {
        "text": "Fair dealing.",
        "rationale": "Fair dealing is a duty owed to all parties, but it is not the same as protecting a client from harm.",
        "isCorrect": false
      },
      {
        "text": "Loyalty.",
        "rationale": "The duty of loyalty requires a broker to put their client's interests first.",
        "isCorrect": true
      },
      {
        "text": "Confidentiality.",
        "rationale": "Confidentiality is a duty not to disclose information, but it is not the same as protecting from harm.",
        "isCorrect": false
      },
      {
        "text": "Skill, care, and diligence.",
        "rationale": "This duty requires a broker to use professional expertise, but it is not the same as a duty of loyalty.",
        "isCorrect": false
      }
    ],
    "hint": "This is a key fiduciary duty in a single agency relationship."
  },
  {
    "questionNumber": 26,
    "question": "A tenant in a condominium owns a fee simple interest in their unit and an undivided interest in the common areas. This describes which type of ownership?",
    "answerOptions": [
      {
        "text": "Cooperative",
        "rationale": "In a cooperative, the owner holds stock in a corporation and a proprietary lease.",
        "isCorrect": false
      },
      {
        "text": "Joint tenancy",
        "rationale": "Joint tenancy is a form of co-ownership, but it does not apply to a condominium unit and common areas.",
        "isCorrect": false
      },
      {
        "text": "Condominium",
        "rationale": "This is the legal definition of condominium ownership.",
        "isCorrect": true
      },
      {
        "text": "Tenancy in common",
        "rationale": "Tenancy in common is a form of co-ownership, but it does not accurately describe the fee simple ownership of a unit.",
        "isCorrect": false
      }
    ],
    "hint": "This is the most common form of ownership for a unit in a shared building."
  },
  {
    "questionNumber": 27,
    "question": "Which of the following would be considered a general lien?",
    "answerOptions": [
      {
        "text": "A property tax lien.",
        "rationale": "A property tax lien is a specific lien.",
        "isCorrect": false
      },
      {
        "text": "A mortgage lien.",
        "rationale": "A mortgage lien is a specific lien.",
        "isCorrect": false
      },
      {
        "text": "A mechanic's lien.",
        "rationale": "A mechanic's lien is a specific lien.",
        "isCorrect": false
      },
      {
        "text": "A judgment lien.",
        "rationale": "A judgment lien is a general lien because it is attached to all of a person's property, both real and personal.",
        "isCorrect": true
      }
    ],
    "hint": "A general lien is not attached to a single property."
  },
  {
    "questionNumber": 28,
    "question": "What is the primary purpose of the 'lot and block' method of legal description?",
    "answerOptions": [
      {
        "text": "To describe a property's boundaries using a point of beginning.",
        "rationale": "This describes the metes and bounds method.",
        "isCorrect": false
      },
      {
        "text": "To identify properties in a subdivision by referencing a recorded plat map.",
        "rationale": "The lot and block method uses a plat map to provide a precise description of a property's location within a subdivision.",
        "isCorrect": true
      },
      {
        "text": "To describe a property using a grid system of townships and sections.",
        "rationale": "This describes the Government Survey System.",
        "isCorrect": false
      },
      {
        "text": "To provide a general location of a property for a street address.",
        "rationale": "A street address is not a legal description.",
        "isCorrect": false
      }
    ],
    "hint": "This method is common for suburban properties."
  },
  {
    "questionNumber": 29,
    "question": "What is the required continuing education for an active sales associate in Florida every two years?",
    "answerOptions": [
      {
        "text": "45 hours of post-licensing education.",
        "rationale": "This is the requirement for the first renewal, not every renewal after that.",
        "isCorrect": false
      },
      {
        "text": "24 hours of continuing education.",
        "rationale": "This is not the required number of hours.",
        "isCorrect": false
      },
      {
        "text": "14 hours of continuing education.",
        "rationale": "The standard continuing education requirement for all active licensees after their first renewal is 14 hours every two years.",
        "isCorrect": true
      },
      {
        "text": "10 hours of continuing education.",
        "rationale": "This is not the required number of hours.",
        "isCorrect": false
      }
    ],
    "hint": "This is the standard, ongoing education requirement."
  },
  {
    "questionNumber": 30,
    "question": "Which of the following is considered a 'trade fixture'?",
    "answerOptions": [
      {
        "text": "A built-in dishwasher in a kitchen.",
        "rationale": "This is a regular fixture.",
        "isCorrect": false
      },
      {
        "text": "A bookshelf that is screwed into the wall of a home.",
        "rationale": "This is a regular fixture.",
        "isCorrect": false
      },
      {
        "text": "A pizza oven installed by a tenant in a commercial kitchen.",
        "rationale": "A trade fixture is an item of personal property installed by a tenant for their business, and it remains the tenant's property.",
        "isCorrect": true
      },
      {
        "text": "A window-unit air conditioner.",
        "rationale": "This is typically considered personal property, but not a trade fixture.",
        "isCorrect": false
      }
    ],
    "hint": "This is an item related to a tenant's business."
  },
  {
    "questionNumber": 31,
    "question": "What is a 'capitalization rate' primarily used for?",
    "answerOptions": [
      {
        "text": "To determine the tax rate on a property.",
        "rationale": "The tax rate is known as the millage rate and is not determined by the capitalization rate.",
        "isCorrect": false
      },
      {
        "text": "To estimate the value of an income-producing property.",
        "rationale": "The capitalization rate is a key component of the income capitalization approach to appraisal, which is used for income-producing properties.",
        "isCorrect": true
      },
      {
        "text": "To calculate the loan-to-value ratio for a mortgage.",
        "rationale": "The LTV ratio is calculated by dividing the loan amount by the property's value.",
        "isCorrect": false
      },
      {
        "text": "To measure the rate of inflation in the housing market.",
        "rationale": "The capitalization rate is a measure of return on investment, not inflation.",
        "isCorrect": false
      }
    ],
    "hint": "This is part of the Income Capitalization Approach."
  },
  {
    "questionNumber": 32,
    "question": "What is the penalty for unlicensed real estate activity in Florida?",
    "answerOptions": [
      {
        "text": "A first-degree misdemeanor.",
        "rationale": "Unlicensed real estate activity is a third-degree felony.",
        "isCorrect": false
      },
      {
        "text": "A third-degree felony.",
        "rationale": "Unlicensed real estate activity is a third-degree felony, punishable by a fine and/or up to five years in prison.",
        "isCorrect": true
      },
      {
        "text": "A fine of up to $1,000.",
        "rationale": "This is the penalty for a first-degree misdemeanor, not a third-degree felony.",
        "isCorrect": false
      },
      {
        "text": "License suspension.",
        "rationale": "This is a penalty for a licensed professional, not an unlicensed person.",
        "isCorrect": false
      }
    ],
    "hint": "This is one of the most serious violations of Florida real estate law."
  },
  {
    "questionNumber": 33,
    "question": "What is 'commingling' of funds?",
    "answerOptions": [
      {
        "text": "A broker depositing a client's funds into their personal checking account.",
        "rationale": "Mixing a client's funds with a broker's own personal or business funds is commingling.",
        "isCorrect": true
      },
      {
        "text": "A broker using a client's escrow funds to pay office rent.",
        "rationale": "This is a more severe offense known as conversion.",
        "isCorrect": false
      },
      {
        "text": "A sales associate holding a client's escrow funds in a personal safe for a week.",
        "rationale": "This is a failure to properly handle funds, but it is not commingling.",
        "isCorrect": false
      },
      {
        "text": "A broker depositing a client's funds into a separate, interest-bearing escrow account with written consent.",
        "rationale": "This is a legal way to handle a client's funds.",
        "isCorrect": false
      }
    ],
    "hint": "This is the act of 'mixing' money."
  },
  {
    "questionNumber": 34,
    "question": "What is the minimum age requirement to obtain a real estate license in Florida?",
    "answerOptions": [
      {
        "text": "16 years old",
        "rationale": "16 is too young for a real estate license in Florida.",
        "isCorrect": false
      },
      {
        "text": "18 years old",
        "rationale": "The minimum age requirement for a real estate license in Florida is 18 years old.",
        "isCorrect": true
      },
      {
        "text": "21 years old",
        "rationale": "21 is not the minimum age requirement in Florida.",
        "isCorrect": false
      },
      {
        "text": "25 years old",
        "rationale": "25 is not the minimum age requirement in Florida.",
        "isCorrect": false
      }
    ],
    "hint": "Consider the legal age of majority in most states."
  },
  {
    "questionNumber": 35,
    "question": "In Florida, how many hours of pre-licensing education are required for a sales associate license?",
    "answerOptions": [
      {
        "text": "60 hours",
        "rationale": "60 hours is not the correct requirement for Florida sales associate pre-licensing education.",
        "isCorrect": false
      },
      {
        "text": "63 hours",
        "rationale": "Florida requires 63 hours of pre-licensing education for a sales associate license.",
        "isCorrect": true
      },
      {
        "text": "72 hours",
        "rationale": "72 hours is not the correct requirement for Florida sales associate pre-licensing education.",
        "isCorrect": false
      },
      {
        "text": "90 hours",
        "rationale": "90 hours is not the correct requirement for Florida sales associate pre-licensing education.",
        "isCorrect": false
      }
    ],
    "hint": "The number is slightly more than 60 but less than 72."
  },
  {
    "questionNumber": 36,
    "question": "What is the primary purpose of the Florida Real Estate Commission (FREC)?",
    "answerOptions": [
      {
        "text": "To collect licensing fees and generate state revenue",
        "rationale": "While FREC collects fees, its primary purpose is regulatory, not revenue generation.",
        "isCorrect": false
      },
      {
        "text": "To regulate and oversee real estate licensees in Florida",
        "rationale": "FREC's primary purpose is to regulate real estate licensees and protect the public interest.",
        "isCorrect": true
      },
      {
        "text": "To provide continuing education courses for licensees",
        "rationale": "FREC approves continuing education providers but doesn't directly provide courses.",
        "isCorrect": false
      },
      {
        "text": "To set property values and tax assessments",
        "rationale": "Property valuation and tax assessment are not FREC's responsibilities.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'commission' means in a regulatory context."
  },
  {
    "questionNumber": 37,
    "question": "In Florida, what is the maximum amount of time a broker can hold earnest money deposits before depositing them?",
    "answerOptions": [
      {
        "text": "24 hours",
        "rationale": "24 hours is too short for the maximum holding period.",
        "isCorrect": false
      },
      {
        "text": "48 hours",
        "rationale": "48 hours is not the correct maximum holding period in Florida.",
        "isCorrect": false
      },
      {
        "text": "End of next business day",
        "rationale": "Florida requires earnest money deposits to be deposited by the end of the next business day after receipt.",
        "isCorrect": true
      },
      {
        "text": "7 days",
        "rationale": "7 days is too long for the maximum holding period.",
        "isCorrect": false
      }
    ],
    "hint": "Consider what constitutes a reasonable business timeframe."
  },
  {
    "questionNumber": 38,
    "question": "What is the primary difference between a broker and a sales associate in Florida?",
    "answerOptions": [
      {
        "text": "A broker can work independently while a sales associate must work under a broker",
        "rationale": "This is the primary difference - brokers can operate independently, sales associates must work under a broker.",
        "isCorrect": true
      },
      {
        "text": "A broker earns more commission than a sales associate",
        "rationale": "Commission structure is not the primary legal difference between broker and sales associate.",
        "isCorrect": false
      },
      {
        "text": "A broker has more education requirements than a sales associate",
        "rationale": "While brokers may have additional requirements, the primary difference is independence.",
        "isCorrect": false
      },
      {
        "text": "A broker can only work in commercial real estate",
        "rationale": "Brokers can work in both residential and commercial real estate.",
        "isCorrect": false
      }
    ],
    "hint": "Think about who can operate a real estate business independently."
  },
  {
    "questionNumber": 39,
    "question": "In Florida, what is required for a real estate license to be considered 'active'?",
    "answerOptions": [
      {
        "text": "The licensee must be employed by a brokerage firm",
        "rationale": "Employment is not required for an active license in Florida.",
        "isCorrect": false
      },
      {
        "text": "The licensee must complete continuing education requirements",
        "rationale": "Continuing education is required for renewal, not for active status.",
        "isCorrect": false
      },
      {
        "text": "The licensee must be associated with a licensed broker",
        "rationale": "In Florida, a license is active if it's current and not suspended, regardless of association.",
        "isCorrect": false
      },
      {
        "text": "The license must be current and not suspended or revoked",
        "rationale": "An active license in Florida is one that is current and not suspended or revoked.",
        "isCorrect": true
      }
    ],
    "hint": "Consider what makes a license legally valid and usable."
  },
  {
    "questionNumber": 40,
    "question": "What is the primary purpose of a 'disclosure' in real estate transactions?",
    "answerOptions": [
      {
        "text": "To protect the seller from future liability",
        "rationale": "While disclosures may provide some protection, their primary purpose is consumer protection.",
        "isCorrect": false
      },
      {
        "text": "To inform buyers about material facts that could affect their decision",
        "rationale": "Disclosures are designed to inform buyers about material facts that could influence their purchase decision.",
        "isCorrect": true
      },
      {
        "text": "To increase the property's market value",
        "rationale": "Disclosures are not designed to increase property value.",
        "isCorrect": false
      },
      {
        "text": "To satisfy lender requirements",
        "rationale": "While lenders may require certain disclosures, their primary purpose is buyer protection.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what information buyers need to make informed decisions."
  },
  {
    "questionNumber": 41,
    "question": "In Florida, what is the maximum commission rate that can be charged?",
    "answerOptions": [
      {
        "text": "There is no maximum commission rate set by law",
        "rationale": "Florida does not set a maximum commission rate - it's determined by market forces and negotiation.",
        "isCorrect": true
      },
      {
        "text": "6% of the sales price",
        "rationale": "6% is a common rate but not a legal maximum in Florida.",
        "isCorrect": false
      },
      {
        "text": "10% of the sales price",
        "rationale": "10% is not a legal maximum commission rate in Florida.",
        "isCorrect": false
      },
      {
        "text": "15% of the sales price",
        "rationale": "15% is not a legal maximum commission rate in Florida.",
        "isCorrect": false
      }
    ],
    "hint": "Consider whether real estate commissions are regulated by law or market forces."
  },
  {
    "questionNumber": 42,
    "question": "What is the primary purpose of a 'title insurance' policy?",
    "answerOptions": [
      {
        "text": "To protect the lender from property damage",
        "rationale": "Title insurance protects against title defects, not property damage.",
        "isCorrect": false
      },
      {
        "text": "To protect against title defects and ownership disputes",
        "rationale": "Title insurance protects against title defects, liens, and ownership disputes that could affect property ownership.",
        "isCorrect": true
      },
      {
        "text": "To cover the cost of property taxes",
        "rationale": "Title insurance does not cover property taxes.",
        "isCorrect": false
      },
      {
        "text": "To insure the property's market value",
        "rationale": "Title insurance does not insure market value - that's property insurance.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'title' means in real estate and what could go wrong with it."
  },
  {
    "questionNumber": 43,
    "question": "In Florida, what is required for a real estate contract to be legally enforceable?",
    "answerOptions": [
      {
        "text": "The contract must be in writing and signed by both parties",
        "rationale": "While writing is important, this alone doesn't make a contract enforceable in Florida.",
        "isCorrect": false
      },
      {
        "text": "The contract must include consideration, mutual assent, and legal capacity",
        "rationale": "A legally enforceable contract requires consideration, mutual assent, legal capacity, and legal purpose.",
        "isCorrect": true
      },
      {
        "text": "The contract must be notarized",
        "rationale": "Notarization is not required for most real estate contracts to be enforceable.",
        "isCorrect": false
      },
      {
        "text": "The contract must be recorded in public records",
        "rationale": "Recording is not required for contract enforceability, though it may be required for certain types of contracts.",
        "isCorrect": false
      }
    ],
    "hint": "Consider the basic elements required for any valid contract."
  },
  {
    "questionNumber": 44,
    "question": "What is the primary purpose of a 'survey' in real estate transactions?",
    "answerOptions": [
      {
        "text": "To determine the property's market value",
        "rationale": "Surveys determine property boundaries, not market value.",
        "isCorrect": false
      },
      {
        "text": "To establish property boundaries and identify encroachments",
        "rationale": "Surveys establish property boundaries and identify any encroachments or boundary disputes.",
        "isCorrect": true
      },
      {
        "text": "To assess the property's structural condition",
        "rationale": "Surveys don't assess structural condition - that's an inspection.",
        "isCorrect": false
      },
      {
        "text": "To determine property tax assessments",
        "rationale": "Surveys don't determine tax assessments.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'surveying' means in the context of land measurement."
  },
  {
    "questionNumber": 45,
    "question": "In Florida, what is the primary responsibility of a 'transaction broker'?",
    "answerOptions": [
      {
        "text": "To represent only the buyer's interests",
        "rationale": "Transaction brokers don't represent only one party - they facilitate transactions.",
        "isCorrect": false
      },
      {
        "text": "To represent only the seller's interests",
        "rationale": "Transaction brokers don't represent only one party - they facilitate transactions.",
        "isCorrect": false
      },
      {
        "text": "To facilitate the transaction without representing either party",
        "rationale": "Transaction brokers facilitate transactions without representing either party's interests.",
        "isCorrect": true
      },
      {
        "text": "To represent both parties equally",
        "rationale": "Representing both parties equally would be dual agency, not transaction brokerage.",
        "isCorrect": false
      }
    ],
    "hint": "Consider what 'transaction' means versus 'representation' in real estate."
  },
  {
    "questionNumber": 46,
    "question": "What is the primary purpose of a 'mortgage' in real estate financing?",
    "answerOptions": [
      {
        "text": "To provide insurance for the property",
        "rationale": "Mortgages are loans, not insurance policies.",
        "isCorrect": false
      },
      {
        "text": "To create a lien on the property as security for a loan",
        "rationale": "A mortgage creates a lien on the property as security for repayment of a loan.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property to the lender",
        "rationale": "Mortgages don't transfer ownership - they create a security interest.",
        "isCorrect": false
      },
      {
        "text": "To establish the property's market value",
        "rationale": "Mortgages don't establish market value - that's an appraisal.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what security means in the context of borrowing money."
  },
  {
    "questionNumber": 47,
    "question": "In Florida, what is required for a real estate license renewal?",
    "answerOptions": [
      {
        "text": "Completion of 14 hours of continuing education every two years",
        "rationale": "Florida requires 14 hours of continuing education every two years for license renewal.",
        "isCorrect": true
      },
      {
        "text": "Completion of 20 hours of continuing education every year",
        "rationale": "20 hours annually is not the correct requirement for Florida license renewal.",
        "isCorrect": false
      },
      {
        "text": "Passing the state exam again",
        "rationale": "Re-taking the exam is not required for license renewal in Florida.",
        "isCorrect": false
      },
      {
        "text": "No continuing education is required",
        "rationale": "Continuing education is required for license renewal in Florida.",
        "isCorrect": false
      }
    ],
    "hint": "Consider what Florida requires to keep a license current."
  },
  {
    "questionNumber": 48,
    "question": "What is the primary purpose of a 'deed' in real estate transactions?",
    "answerOptions": [
      {
        "text": "To create a lien on the property",
        "rationale": "Deeds transfer ownership, they don't create liens.",
        "isCorrect": false
      },
      {
        "text": "To transfer ownership of real property from one party to another",
        "rationale": "A deed is the legal instrument that transfers ownership of real property.",
        "isCorrect": true
      },
      {
        "text": "To establish the property's boundaries",
        "rationale": "Deeds don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To secure a loan on the property",
        "rationale": "Deeds don't secure loans - that's a mortgage.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what document actually transfers property ownership."
  },
  {
    "questionNumber": 49,
    "question": "In Florida, what is the primary purpose of the 'Good Faith Estimate' (GFE)?",
    "answerOptions": [
      {
        "text": "To establish the property's market value",
        "rationale": "GFE estimates loan costs, not property value.",
        "isCorrect": false
      },
      {
        "text": "To provide an estimate of loan costs and fees to the borrower",
        "rationale": "The GFE provides borrowers with an estimate of loan costs and fees before closing.",
        "isCorrect": true
      },
      {
        "text": "To determine the borrower's creditworthiness",
        "rationale": "GFE doesn't determine creditworthiness - that's the loan application process.",
        "isCorrect": false
      },
      {
        "text": "To establish the interest rate on the loan",
        "rationale": "GFE estimates costs but doesn't establish the interest rate.",
        "isCorrect": false
      }
    ],
    "hint": "Consider what 'good faith' and 'estimate' mean in lending."
  },
  {
    "questionNumber": 50,
    "question": "What is the primary difference between 'real property' and 'personal property'?",
    "answerOptions": [
      {
        "text": "Real property is more valuable than personal property",
        "rationale": "Value is not the primary distinction between real and personal property.",
        "isCorrect": false
      },
      {
        "text": "Real property is immovable while personal property is movable",
        "rationale": "Real property is land and anything permanently attached to it (immovable), while personal property is movable.",
        "isCorrect": true
      },
      {
        "text": "Real property requires a deed while personal property doesn't",
        "rationale": "While real property typically requires a deed, this isn't the primary distinction.",
        "isCorrect": false
      },
      {
        "text": "Real property is subject to property taxes while personal property isn't",
        "rationale": "Both real and personal property can be subject to taxes, so this isn't the primary distinction.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what makes something 'real' versus 'personal' in legal terms."
  },
  {
    "questionNumber": 51,
    "question": "In Florida, what is the primary purpose of a 'closing statement'?",
    "answerOptions": [
      {
        "text": "To establish the property's market value",
        "rationale": "Closing statements show financial details, not market value.",
        "isCorrect": false
      },
      {
        "text": "To provide a detailed accounting of all financial transactions at closing",
        "rationale": "A closing statement provides a detailed accounting of all financial transactions, including costs, credits, and net amounts.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property",
        "rationale": "Closing statements don't transfer ownership - that's the deed.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the purchase",
        "rationale": "Closing statements don't secure financing - that's the loan application and approval process.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what financial document summarizes all the money involved in a real estate transaction."
  },
  {
    "questionNumber": 52,
    "question": "What is the primary purpose of a 'property inspection' in real estate transactions?",
    "answerOptions": [
      {
        "text": "To determine the property's market value",
        "rationale": "Inspections assess condition, not market value.",
        "isCorrect": false
      },
      {
        "text": "To assess the physical condition and identify potential problems",
        "rationale": "Property inspections assess the physical condition and identify potential problems or defects.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Inspections don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To determine property tax assessments",
        "rationale": "Inspections don't determine tax assessments.",
        "isCorrect": false
      }
    ],
    "hint": "Consider what a home inspector is looking for when examining a property."
  },
  {
    "questionNumber": 53,
    "question": "In Florida, what is the primary responsibility of a 'dual agent'?",
    "answerOptions": [
      {
        "text": "To represent only the buyer's interests",
        "rationale": "Dual agents don't represent only one party - they represent both.",
        "isCorrect": false
      },
      {
        "text": "To represent only the seller's interests",
        "rationale": "Dual agents don't represent only one party - they represent both.",
        "isCorrect": false
      },
      {
        "text": "To represent both parties with equal loyalty and confidentiality",
        "rationale": "Dual agents represent both parties with equal loyalty and must maintain confidentiality for both.",
        "isCorrect": true
      },
      {
        "text": "To facilitate the transaction without representing either party",
        "rationale": "This describes transaction brokerage, not dual agency.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'dual' means in the context of representation."
  },
  {
    "questionNumber": 54,
    "question": "What is the primary purpose of a 'lien' in real estate?",
    "answerOptions": [
      {
        "text": "To transfer ownership of the property",
        "rationale": "Liens don't transfer ownership - they create a claim against the property.",
        "isCorrect": false
      },
      {
        "text": "To create a legal claim against the property as security for a debt",
        "rationale": "A lien creates a legal claim against the property as security for repayment of a debt.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Liens don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To determine the property's market value",
        "rationale": "Liens don't determine market value - that's an appraisal.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what happens when someone owes money and uses property as collateral."
  },
  {
    "questionNumber": 55,
    "question": "In Florida, what is the primary purpose of the 'Truth in Lending Act' (TILA)?",
    "answerOptions": [
      {
        "text": "To regulate real estate commissions",
        "rationale": "TILA doesn't regulate real estate commissions - it's about lending disclosure.",
        "isCorrect": false
      },
      {
        "text": "To require disclosure of loan terms and costs to borrowers",
        "rationale": "TILA requires lenders to disclose loan terms, costs, and annual percentage rates to borrowers.",
        "isCorrect": true
      },
      {
        "text": "To establish maximum interest rates on loans",
        "rationale": "TILA doesn't establish maximum interest rates - it's about disclosure.",
        "isCorrect": false
      },
      {
        "text": "To regulate property appraisals",
        "rationale": "TILA doesn't regulate appraisals - it's about lending disclosure.",
        "isCorrect": false
      }
    ],
    "hint": "Consider what 'truth' and 'lending' mean in the context of consumer protection."
  },
  {
    "questionNumber": 56,
    "question": "What is the primary purpose of a 'warranty deed' in real estate transactions?",
    "answerOptions": [
      {
        "text": "To create a lien on the property",
        "rationale": "Warranty deeds transfer ownership, they don't create liens.",
        "isCorrect": false
      },
      {
        "text": "To transfer ownership with guarantees about the title",
        "rationale": "A warranty deed transfers ownership and provides guarantees about the title's validity and freedom from defects.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Warranty deeds don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To secure a loan on the property",
        "rationale": "Warranty deeds don't secure loans - that's a mortgage.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'warranty' means in the context of a deed."
  },
  {
    "questionNumber": 57,
    "question": "In Florida, what is the primary purpose of the 'Real Estate Settlement Procedures Act' (RESPA)?",
    "answerOptions": [
      {
        "text": "To regulate real estate commissions",
        "rationale": "RESPA doesn't regulate commissions - it's about settlement procedures.",
        "isCorrect": false
      },
      {
        "text": "To protect consumers in real estate transactions involving federally related mortgage loans",
        "rationale": "RESPA protects consumers by regulating settlement procedures and prohibiting kickbacks in federally related mortgage loans.",
        "isCorrect": true
      },
      {
        "text": "To establish property values",
        "rationale": "RESPA doesn't establish property values - that's an appraisal.",
        "isCorrect": false
      },
      {
        "text": "To regulate property inspections",
        "rationale": "RESPA doesn't regulate inspections - it's about settlement procedures.",
        "isCorrect": false
      }
    ],
    "hint": "Consider what 'settlement procedures' means in real estate transactions."
  },
  {
    "questionNumber": 58,
    "question": "What is the primary purpose of a 'quitclaim deed' in real estate transactions?",
    "answerOptions": [
      {
        "text": "To transfer ownership with full warranties about the title",
        "rationale": "Quitclaim deeds don't provide warranties - they transfer whatever interest the grantor has.",
        "isCorrect": false
      },
      {
        "text": "To transfer whatever interest the grantor has without warranties",
        "rationale": "A quitclaim deed transfers whatever interest the grantor has in the property without any warranties about the title.",
        "isCorrect": true
      },
      {
        "text": "To create a lien on the property",
        "rationale": "Quitclaim deeds transfer ownership, they don't create liens.",
        "isCorrect": false
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Quitclaim deeds don't establish boundaries - that's a survey.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'quit' and 'claim' mean in the context of transferring rights."
  },
  {
    "questionNumber": 59,
    "question": "In Florida, what is the primary purpose of a 'property disclosure statement'?",
    "answerOptions": [
      {
        "text": "To establish the property's market value",
        "rationale": "Disclosure statements don't establish market value - they disclose known defects.",
        "isCorrect": false
      },
      {
        "text": "To inform buyers about known material defects in the property",
        "rationale": "Property disclosure statements inform buyers about known material defects that could affect their decision to purchase.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property",
        "rationale": "Disclosure statements don't transfer ownership - that's the deed.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the purchase",
        "rationale": "Disclosure statements don't secure financing - that's the loan application process.",
        "isCorrect": false
      }
    ],
    "hint": "Consider what 'disclosure' means in the context of informing buyers about property conditions."
  },
  {
    "questionNumber": 60,
    "question": "What is the primary purpose of a 'mortgage broker' in real estate financing?",
    "answerOptions": [
      {
        "text": "To originate loans directly to borrowers",
        "rationale": "Mortgage brokers don't originate loans directly - they arrange loans with lenders.",
        "isCorrect": false
      },
      {
        "text": "To arrange loans between borrowers and lenders",
        "rationale": "Mortgage brokers arrange loans between borrowers and lenders but don't fund the loans themselves.",
        "isCorrect": true
      },
      {
        "text": "To appraise property values",
        "rationale": "Mortgage brokers don't appraise properties - that's an appraiser's job.",
        "isCorrect": false
      },
      {
        "text": "To inspect properties for defects",
        "rationale": "Mortgage brokers don't inspect properties - that's an inspector's job.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'broker' means in the context of arranging transactions."
  },
  {
    "questionNumber": 61,
    "question": "In Florida, what is the primary purpose of a 'title search'?",
    "answerOptions": [
      {
        "text": "To determine the property's market value",
        "rationale": "Title searches don't determine market value - they examine ownership history.",
        "isCorrect": false
      },
      {
        "text": "To examine the property's ownership history and identify any liens or encumbrances",
        "rationale": "A title search examines the property's ownership history and identifies any liens, encumbrances, or title defects.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Title searches don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To assess the property's physical condition",
        "rationale": "Title searches don't assess physical condition - that's an inspection.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'title' means in real estate and what could be wrong with it."
  },
  {
    "questionNumber": 62,
    "question": "What is the primary purpose of a 'lease agreement' in real estate?",
    "answerOptions": [
      {
        "text": "To transfer ownership of the property",
        "rationale": "Lease agreements don't transfer ownership - they create rental arrangements.",
        "isCorrect": false
      },
      {
        "text": "To create a rental arrangement between landlord and tenant",
        "rationale": "A lease agreement creates a rental arrangement between landlord and tenant, establishing rights and obligations.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Lease agreements don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To secure a loan on the property",
        "rationale": "Lease agreements don't secure loans - that's a mortgage.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what document governs rental relationships."
  },
  {
    "questionNumber": 63,
    "question": "In Florida, what is the primary purpose of a 'property appraisal'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Appraisals don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To determine the property's market value",
        "rationale": "A property appraisal determines the property's market value based on comparable sales and other factors.",
        "isCorrect": true
      },
      {
        "text": "To assess the property's physical condition",
        "rationale": "While appraisers note condition, their primary purpose is value determination, not condition assessment.",
        "isCorrect": false
      },
      {
        "text": "To identify title defects",
        "rationale": "Appraisals don't identify title defects - that's a title search.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'appraisal' means in the context of determining worth."
  },
  {
    "questionNumber": 64,
    "question": "What is the primary purpose of a 'property management agreement'?",
    "answerOptions": [
      {
        "text": "To transfer ownership of the property",
        "rationale": "Management agreements don't transfer ownership - they create management relationships.",
        "isCorrect": false
      },
      {
        "text": "To create a relationship between property owner and manager",
        "rationale": "A property management agreement creates a relationship between property owner and manager, establishing duties and responsibilities.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Management agreements don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To secure a loan on the property",
        "rationale": "Management agreements don't secure loans - that's a mortgage.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what document governs property management relationships."
  },
  {
    "questionNumber": 65,
    "question": "In Florida, what is the primary purpose of a 'real estate contract'?",
    "answerOptions": [
      {
        "text": "To transfer ownership of the property",
        "rationale": "Contracts don't transfer ownership - they create obligations to transfer ownership.",
        "isCorrect": false
      },
      {
        "text": "To create legally binding obligations between buyer and seller",
        "rationale": "A real estate contract creates legally binding obligations between buyer and seller regarding the property transaction.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Contracts don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the purchase",
        "rationale": "Contracts don't secure financing - that's the loan application process.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what document creates obligations in a real estate transaction."
  },
  {
    "questionNumber": 66,
    "question": "What is the primary purpose of a 'property tax assessment'?",
    "answerOptions": [
      {
        "text": "To determine the property's market value",
        "rationale": "While assessments are based on value, their primary purpose is tax calculation, not market value determination.",
        "isCorrect": false
      },
      {
        "text": "To calculate property taxes owed to the government",
        "rationale": "Property tax assessments determine the value used to calculate property taxes owed to the government.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Tax assessments don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To identify title defects",
        "rationale": "Tax assessments don't identify title defects - that's a title search.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'tax assessment' means in the context of government revenue."
  },
  {
    "questionNumber": 67,
    "question": "In Florida, what is the primary purpose of a 'real estate license'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Licenses don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To authorize individuals to engage in real estate brokerage activities",
        "rationale": "A real estate license authorizes individuals to engage in real estate brokerage activities legally.",
        "isCorrect": true
      },
      {
        "text": "To determine property values",
        "rationale": "Licenses don't determine values - that's an appraisal.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for purchases",
        "rationale": "Licenses don't secure financing - that's the loan application process.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what authorization is needed to legally practice real estate."
  },
  {
    "questionNumber": 68,
    "question": "What is the primary purpose of a 'property insurance policy'?",
    "answerOptions": [
      {
        "text": "To protect against title defects",
        "rationale": "Property insurance doesn't protect against title defects - that's title insurance.",
        "isCorrect": false
      },
      {
        "text": "To protect against physical damage to the property",
        "rationale": "Property insurance protects against physical damage to the property from covered perils.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Insurance doesn't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To determine property values",
        "rationale": "Insurance doesn't determine values - that's an appraisal.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'property insurance' protects against."
  },
  {
    "questionNumber": 69,
    "question": "In Florida, what is the primary purpose of a 'real estate closing'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Closings don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To complete the transfer of property ownership and finalize the transaction",
        "rationale": "A real estate closing completes the transfer of property ownership and finalizes all aspects of the transaction.",
        "isCorrect": true
      },
      {
        "text": "To determine property values",
        "rationale": "Closings don't determine values - that's an appraisal.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the purchase",
        "rationale": "Closings don't secure financing - that's the loan application process.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what happens at the end of a real estate transaction."
  },
  {
    "questionNumber": 70,
    "question": "What is the primary purpose of a 'property deed restriction'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Deed restrictions don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To limit how the property can be used",
        "rationale": "Deed restrictions limit how the property can be used and are binding on future owners.",
        "isCorrect": true
      },
      {
        "text": "To determine property values",
        "rationale": "Deed restrictions don't determine values - that's an appraisal.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the property",
        "rationale": "Deed restrictions don't secure financing - that's a mortgage.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'restriction' means in the context of property use."
  },
  {
    "questionNumber": 71,
    "question": "In Florida, what is the primary purpose of a 'real estate appraisal'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Appraisals don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To determine the property's fair market value",
        "rationale": "A real estate appraisal determines the property's fair market value based on comparable sales and other factors.",
        "isCorrect": true
      },
      {
        "text": "To identify title defects",
        "rationale": "Appraisals don't identify title defects - that's a title search.",
        "isCorrect": false
      },
      {
        "text": "To assess the property's physical condition",
        "rationale": "While appraisers note condition, their primary purpose is value determination, not condition assessment.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'appraisal' means in the context of determining worth."
  },
  {
    "questionNumber": 72,
    "question": "What is the primary purpose of a 'real estate listing agreement'?",
    "answerOptions": [
      {
        "text": "To transfer ownership of the property",
        "rationale": "Listing agreements don't transfer ownership - they create agency relationships.",
        "isCorrect": false
      },
      {
        "text": "To create an agency relationship between seller and broker",
        "rationale": "A listing agreement creates an agency relationship between seller and broker for the sale of the property.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Listing agreements don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the property",
        "rationale": "Listing agreements don't secure financing - that's a mortgage.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what document creates the relationship between a seller and their real estate agent."
  },
  {
    "questionNumber": 73,
    "question": "In Florida, what is the primary purpose of a 'real estate purchase agreement'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Purchase agreements don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To create legally binding obligations between buyer and seller",
        "rationale": "A purchase agreement creates legally binding obligations between buyer and seller regarding the property transaction.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property",
        "rationale": "Purchase agreements don't transfer ownership - they create obligations to transfer ownership.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the purchase",
        "rationale": "Purchase agreements don't secure financing - that's the loan application process.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what document creates obligations in a real estate purchase."
  },
  {
    "questionNumber": 74,
    "question": "What is the primary purpose of a 'real estate escrow account'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Escrow accounts don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To hold funds in trust during a real estate transaction",
        "rationale": "An escrow account holds funds in trust during a real estate transaction until closing conditions are met.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property",
        "rationale": "Escrow accounts don't transfer ownership - they hold funds.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the purchase",
        "rationale": "Escrow accounts don't secure financing - that's the loan application process.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'escrow' means in the context of holding something in trust."
  },
  {
    "questionNumber": 75,
    "question": "In Florida, what is the primary purpose of a 'real estate disclosure'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Disclosures don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To inform buyers about material facts that could affect their decision",
        "rationale": "Real estate disclosures inform buyers about material facts that could affect their decision to purchase.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property",
        "rationale": "Disclosures don't transfer ownership - that's the deed.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the purchase",
        "rationale": "Disclosures don't secure financing - that's the loan application process.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'disclosure' means in the context of informing buyers."
  },
  {
    "questionNumber": 76,
    "question": "What is the primary purpose of a 'real estate title insurance policy'?",
    "answerOptions": [
      {
        "text": "To protect against physical damage to the property",
        "rationale": "Title insurance doesn't protect against physical damage - that's property insurance.",
        "isCorrect": false
      },
      {
        "text": "To protect against title defects and ownership disputes",
        "rationale": "Title insurance protects against title defects, liens, and ownership disputes that could affect property ownership.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Title insurance doesn't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To determine property values",
        "rationale": "Title insurance doesn't determine values - that's an appraisal.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'title' means in real estate and what could go wrong with it."
  },
  {
    "questionNumber": 77,
    "question": "In Florida, what is the primary purpose of a 'real estate survey'?",
    "answerOptions": [
      {
        "text": "To determine the property's market value",
        "rationale": "Surveys don't determine market value - that's an appraisal.",
        "isCorrect": false
      },
      {
        "text": "To establish property boundaries and identify encroachments",
        "rationale": "A real estate survey establishes property boundaries and identifies any encroachments or boundary disputes.",
        "isCorrect": true
      },
      {
        "text": "To assess the property's physical condition",
        "rationale": "Surveys don't assess physical condition - that's an inspection.",
        "isCorrect": false
      },
      {
        "text": "To identify title defects",
        "rationale": "Surveys don't identify title defects - that's a title search.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'surveying' means in the context of land measurement."
  },
  {
    "questionNumber": 78,
    "question": "What is the primary purpose of a 'real estate inspection'?",
    "answerOptions": [
      {
        "text": "To determine the property's market value",
        "rationale": "Inspections don't determine market value - that's an appraisal.",
        "isCorrect": false
      },
      {
        "text": "To assess the property's physical condition and identify potential problems",
        "rationale": "A real estate inspection assesses the property's physical condition and identifies potential problems or defects.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Inspections don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To identify title defects",
        "rationale": "Inspections don't identify title defects - that's a title search.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what a home inspector is looking for when examining a property."
  },
  {
    "questionNumber": 79,
    "question": "In Florida, what is the primary purpose of a 'real estate closing statement'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Closing statements don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To provide a detailed accounting of all financial transactions at closing",
        "rationale": "A closing statement provides a detailed accounting of all financial transactions, including costs, credits, and net amounts.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property",
        "rationale": "Closing statements don't transfer ownership - that's the deed.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the purchase",
        "rationale": "Closing statements don't secure financing - that's the loan application process.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what financial document summarizes all the money involved in a real estate transaction."
  },
  {
    "questionNumber": 80,
    "question": "What is the primary purpose of a 'real estate mortgage'?",
    "answerOptions": [
      {
        "text": "To transfer ownership of the property",
        "rationale": "Mortgages don't transfer ownership - they create security interests.",
        "isCorrect": false
      },
      {
        "text": "To create a lien on the property as security for a loan",
        "rationale": "A mortgage creates a lien on the property as security for repayment of a loan.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Mortgages don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To determine property values",
        "rationale": "Mortgages don't determine values - that's an appraisal.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what security means in the context of borrowing money."
  },
  {
    "questionNumber": 81,
    "question": "In Florida, what is the primary purpose of a 'real estate deed'?",
    "answerOptions": [
      {
        "text": "To create a lien on the property",
        "rationale": "Deeds don't create liens - they transfer ownership.",
        "isCorrect": false
      },
      {
        "text": "To transfer ownership of real property from one party to another",
        "rationale": "A deed is the legal instrument that transfers ownership of real property.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Deeds don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To secure a loan on the property",
        "rationale": "Deeds don't secure loans - that's a mortgage.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what document actually transfers property ownership."
  },
  {
    "questionNumber": 82,
    "question": "What is the primary purpose of a 'real estate lease'?",
    "answerOptions": [
      {
        "text": "To transfer ownership of the property",
        "rationale": "Leases don't transfer ownership - they create rental arrangements.",
        "isCorrect": false
      },
      {
        "text": "To create a rental arrangement between landlord and tenant",
        "rationale": "A lease creates a rental arrangement between landlord and tenant, establishing rights and obligations.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Leases don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To secure a loan on the property",
        "rationale": "Leases don't secure loans - that's a mortgage.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what document governs rental relationships."
  },
  {
    "questionNumber": 83,
    "question": "In Florida, what is the primary purpose of a 'real estate contract'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Contracts don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To create legally binding obligations between buyer and seller",
        "rationale": "A real estate contract creates legally binding obligations between buyer and seller regarding the property transaction.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property",
        "rationale": "Contracts don't transfer ownership - they create obligations to transfer ownership.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the purchase",
        "rationale": "Contracts don't secure financing - that's the loan application process.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what document creates obligations in a real estate transaction."
  },
  {
    "questionNumber": 84,
    "question": "What is the primary purpose of a 'real estate appraisal'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Appraisals don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To determine the property's fair market value",
        "rationale": "A real estate appraisal determines the property's fair market value based on comparable sales and other factors.",
        "isCorrect": true
      },
      {
        "text": "To assess the property's physical condition",
        "rationale": "While appraisers note condition, their primary purpose is value determination, not condition assessment.",
        "isCorrect": false
      },
      {
        "text": "To identify title defects",
        "rationale": "Appraisals don't identify title defects - that's a title search.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'appraisal' means in the context of determining worth."
  },
  {
    "questionNumber": 85,
    "question": "In Florida, what is the primary purpose of a 'real estate listing'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Listings don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To create an agency relationship between seller and broker",
        "rationale": "A real estate listing creates an agency relationship between seller and broker for the sale of the property.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property",
        "rationale": "Listings don't transfer ownership - they create agency relationships.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the property",
        "rationale": "Listings don't secure financing - that's a mortgage.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what document creates the relationship between a seller and their real estate agent."
  },
  {
    "questionNumber": 86,
    "question": "What is the primary purpose of a 'real estate purchase agreement'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Purchase agreements don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To create legally binding obligations between buyer and seller",
        "rationale": "A purchase agreement creates legally binding obligations between buyer and seller regarding the property transaction.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property",
        "rationale": "Purchase agreements don't transfer ownership - they create obligations to transfer ownership.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the purchase",
        "rationale": "Purchase agreements don't secure financing - that's the loan application process.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what document creates obligations in a real estate purchase."
  },
  {
    "questionNumber": 87,
    "question": "In Florida, what is the primary purpose of a 'real estate escrow'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Escrow doesn't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To hold funds in trust during a real estate transaction",
        "rationale": "Escrow holds funds in trust during a real estate transaction until closing conditions are met.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property",
        "rationale": "Escrow doesn't transfer ownership - it holds funds.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the purchase",
        "rationale": "Escrow doesn't secure financing - that's the loan application process.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'escrow' means in the context of holding something in trust."
  },
  {
    "questionNumber": 88,
    "question": "What is the primary purpose of a 'real estate disclosure'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Disclosures don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To inform buyers about material facts that could affect their decision",
        "rationale": "Real estate disclosures inform buyers about material facts that could affect their decision to purchase.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property",
        "rationale": "Disclosures don't transfer ownership - that's the deed.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the purchase",
        "rationale": "Disclosures don't secure financing - that's the loan application process.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'disclosure' means in the context of informing buyers."
  },
  {
    "questionNumber": 89,
    "question": "In Florida, what is the primary purpose of a 'real estate title search'?",
    "answerOptions": [
      {
        "text": "To determine the property's market value",
        "rationale": "Title searches don't determine market value - they examine ownership history.",
        "isCorrect": false
      },
      {
        "text": "To examine the property's ownership history and identify any liens or encumbrances",
        "rationale": "A title search examines the property's ownership history and identifies any liens, encumbrances, or title defects.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Title searches don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To assess the property's physical condition",
        "rationale": "Title searches don't assess physical condition - that's an inspection.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'title' means in real estate and what could be wrong with it."
  },
  {
    "questionNumber": 90,
    "question": "What is the primary purpose of a 'real estate survey'?",
    "answerOptions": [
      {
        "text": "To determine the property's market value",
        "rationale": "Surveys don't determine market value - that's an appraisal.",
        "isCorrect": false
      },
      {
        "text": "To establish property boundaries and identify encroachments",
        "rationale": "A real estate survey establishes property boundaries and identifies any encroachments or boundary disputes.",
        "isCorrect": true
      },
      {
        "text": "To assess the property's physical condition",
        "rationale": "Surveys don't assess physical condition - that's an inspection.",
        "isCorrect": false
      },
      {
        "text": "To identify title defects",
        "rationale": "Surveys don't identify title defects - that's a title search.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'surveying' means in the context of land measurement."
  },
  {
    "questionNumber": 91,
    "question": "In Florida, what is the primary purpose of a 'real estate inspection'?",
    "answerOptions": [
      {
        "text": "To determine the property's market value",
        "rationale": "Inspections don't determine market value - that's an appraisal.",
        "isCorrect": false
      },
      {
        "text": "To assess the property's physical condition and identify potential problems",
        "rationale": "A real estate inspection assesses the property's physical condition and identifies potential problems or defects.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Inspections don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To identify title defects",
        "rationale": "Inspections don't identify title defects - that's a title search.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what a home inspector is looking for when examining a property."
  },
  {
    "questionNumber": 92,
    "question": "What is the primary purpose of a 'real estate closing statement'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Closing statements don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To provide a detailed accounting of all financial transactions at closing",
        "rationale": "A closing statement provides a detailed accounting of all financial transactions, including costs, credits, and net amounts.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property",
        "rationale": "Closing statements don't transfer ownership - that's the deed.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the purchase",
        "rationale": "Closing statements don't secure financing - that's the loan application process.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what financial document summarizes all the money involved in a real estate transaction."
  },
  {
    "questionNumber": 93,
    "question": "In Florida, what is the primary purpose of a 'real estate mortgage'?",
    "answerOptions": [
      {
        "text": "To transfer ownership of the property",
        "rationale": "Mortgages don't transfer ownership - they create security interests.",
        "isCorrect": false
      },
      {
        "text": "To create a lien on the property as security for a loan",
        "rationale": "A mortgage creates a lien on the property as security for repayment of a loan.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Mortgages don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To determine property values",
        "rationale": "Mortgages don't determine values - that's an appraisal.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what security means in the context of borrowing money."
  },
  {
    "questionNumber": 94,
    "question": "What is the primary purpose of a 'real estate deed'?",
    "answerOptions": [
      {
        "text": "To create a lien on the property",
        "rationale": "Deeds don't create liens - they transfer ownership.",
        "isCorrect": false
      },
      {
        "text": "To transfer ownership of real property from one party to another",
        "rationale": "A deed is the legal instrument that transfers ownership of real property.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Deeds don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To secure a loan on the property",
        "rationale": "Deeds don't secure loans - that's a mortgage.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what document actually transfers property ownership."
  },
  {
    "questionNumber": 95,
    "question": "In Florida, what is the primary purpose of a 'real estate lease'?",
    "answerOptions": [
      {
        "text": "To transfer ownership of the property",
        "rationale": "Leases don't transfer ownership - they create rental arrangements.",
        "isCorrect": false
      },
      {
        "text": "To create a rental arrangement between landlord and tenant",
        "rationale": "A lease creates a rental arrangement between landlord and tenant, establishing rights and obligations.",
        "isCorrect": true
      },
      {
        "text": "To establish property boundaries",
        "rationale": "Leases don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To secure a loan on the property",
        "rationale": "Leases don't secure loans - that's a mortgage.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what document governs rental relationships."
  },
  {
    "questionNumber": 96,
    "question": "What is the primary purpose of a 'real estate contract'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Contracts don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To create legally binding obligations between buyer and seller",
        "rationale": "A real estate contract creates legally binding obligations between buyer and seller regarding the property transaction.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property",
        "rationale": "Contracts don't transfer ownership - they create obligations to transfer ownership.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the purchase",
        "rationale": "Contracts don't secure financing - that's the loan application process.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what document creates obligations in a real estate transaction."
  },
  {
    "questionNumber": 97,
    "question": "In Florida, what is the primary purpose of a 'real estate appraisal'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Appraisals don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To determine the property's fair market value",
        "rationale": "A real estate appraisal determines the property's fair market value based on comparable sales and other factors.",
        "isCorrect": true
      },
      {
        "text": "To assess the property's physical condition",
        "rationale": "While appraisers note condition, their primary purpose is value determination, not condition assessment.",
        "isCorrect": false
      },
      {
        "text": "To identify title defects",
        "rationale": "Appraisals don't identify title defects - that's a title search.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'appraisal' means in the context of determining worth."
  },
  {
    "questionNumber": 98,
    "question": "What is the primary purpose of a 'real estate listing'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Listings don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To create an agency relationship between seller and broker",
        "rationale": "A real estate listing creates an agency relationship between seller and broker for the sale of the property.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property",
        "rationale": "Listings don't transfer ownership - they create agency relationships.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the property",
        "rationale": "Listings don't secure financing - that's a mortgage.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what document creates the relationship between a seller and their real estate agent."
  },
  {
    "questionNumber": 99,
    "question": "In Florida, what is the primary purpose of a 'real estate purchase agreement'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Purchase agreements don't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To create legally binding obligations between buyer and seller",
        "rationale": "A purchase agreement creates legally binding obligations between buyer and seller regarding the property transaction.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property",
        "rationale": "Purchase agreements don't transfer ownership - they create obligations to transfer ownership.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the purchase",
        "rationale": "Purchase agreements don't secure financing - that's the loan application process.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what document creates obligations in a real estate purchase."
  },
  {
    "questionNumber": 100,
    "question": "What is the primary purpose of a 'real estate escrow'?",
    "answerOptions": [
      {
        "text": "To establish property boundaries",
        "rationale": "Escrow doesn't establish boundaries - that's a survey.",
        "isCorrect": false
      },
      {
        "text": "To hold funds in trust during a real estate transaction",
        "rationale": "Escrow holds funds in trust during a real estate transaction until closing conditions are met.",
        "isCorrect": true
      },
      {
        "text": "To transfer ownership of the property",
        "rationale": "Escrow doesn't transfer ownership - it holds funds.",
        "isCorrect": false
      },
      {
        "text": "To secure financing for the purchase",
        "rationale": "Escrow doesn't secure financing - that's the loan application process.",
        "isCorrect": false
      }
    ],
    "hint": "Think about what 'escrow' means in the context of holding something in trust."
  }
];
